import { OpenAPIV3 } from "../../@types";
import { JsonSchemaProperty, JsonSchemaUtils, Objects } from "../../util";

/**
 * Selector for mapping from source to target.
 * @param <TSource> Type of the source object (root level).
 * @param <TTarget> Type of the target object (root level).
 */
export class DataMapperSelector<TSource, TTarget> {

  /**
   * Default constructor creates a new DataMapperSelector.
   * @param source The source field / path
   * @param target The target field / path
   * @param func Optional function that maps from source to target.
   */
  public constructor(source: string | undefined = undefined, target: string | undefined = undefined, func: undefined | ((source: TSource, target: TTarget, selector: DataMapperSelector<TSource, TTarget>) => any) = undefined) {
    this.source = source;
    this.target = target;
    this.func = func;
  }

  /** The path for selecting the source */
  public source: string | undefined;

  /** The path for selecting the target */
  public target: string | undefined;

  /** The JSON schema property, if found, after the selectors have been resolved */
  public sourceProp: JsonSchemaProperty | undefined;

  /** The JSON schema property, if found, after the selectors have been resolved */
  public targetProp: JsonSchemaProperty | undefined;

  /** Hierarchy in the source object that is flattened to the target. */
  public flatten: DataMapperSelector<TSource, TTarget>[];

  /**
   * Custom function for data mapping.
   * @param source The source object (root level).
   * @param target The target object (root level): Possibility to set other fields than just the target field.
   * @param selector This selector object: can be used e.g. for fetching source and target paths for generic functions.
   * @returns The value that will be set to the target field of the target object.
   */
  public func?: (source: TSource, target: TTarget, selector: DataMapperSelector<TSource, TTarget>) => any;

  /**
   * Resolves the selector, i.e. sets the sourceProp and targetProp for types.
   * @param sourceRootSchema Root source schema of the DataMapper or Item schema for flatten selector.
   * @param targetRootSchema Root target schema of the DataMapper
   */
  public resolve(sourceRootSchema: OpenAPIV3.NonArraySchemaObject | undefined, targetRootSchema: OpenAPIV3.NonArraySchemaObject | undefined): void {
    this.sourceProp = sourceRootSchema && this.source ? JsonSchemaUtils.getProperty(sourceRootSchema, this.source) : undefined;
    this.targetProp = targetRootSchema && this.target ? JsonSchemaUtils.getProperty(targetRootSchema, this.target) : undefined;
    if (this.flatten?.length) {
      const flattenSourceSchema = (this.sourceProp?.schema as OpenAPIV3.ArraySchemaObject).items as OpenAPIV3.NonArraySchemaObject;
      this.flatten.forEach((child) => child.resolve(flattenSourceSchema, targetRootSchema));
    }
  }

  /** Does the mapping from source to target */
  public doMap(source: TSource, target: TTarget): void {
    if (this.flatten) {
      return; // Flattening is handled on the parent level (as multiple items is needed).
    }
    let value = null;
    if (this.func) {
      value = this.func(source, target, this);
    } else if (this.source?.trim()) {
      value = Objects.getProperty(source, this.source);
    }
    if (this.target?.trim()) {
      try {
        // TODO: Handle type conversions here
        Objects.setProperty(target, this.target, value);
      } catch (ex) {
        // TODO: Consider adding a validation error here.
      }
    }
  }
}